import React, { useState } from 'react';
import { Bot, Sparkles, ArrowRight } from 'lucide-react';

interface AssistantModeSelectorProps {
  onSelectMode: (mode: 'personalized' | 'general') => void;
}

export const AssistantModeSelector: React.FC<AssistantModeSelectorProps> = ({
  onSelectMode
}) => {
  const [isInitializing, setIsInitializing] = useState(false);

  const handleModeSelect = async (mode: 'personalized' | 'general') => {
    if (mode === 'general') {
      setIsInitializing(true);
    }
    await onSelectMode(mode);
    // Note: If general mode fails, the parent will handle it and we'll stay on this screen
    setIsInitializing(false);
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Welcome to Interview CoPilot
          </h1>
          <p className="text-lg text-gray-600">
            Choose how you'd like to get started with your AI assistant
          </p>
        </div>

        {/* Mode Selection Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Personalized AI Assistant Bot */}
          <div className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all transform hover:-translate-y-1 border-2 border-transparent hover:border-primary-400">
            <div className="flex flex-col h-full">
              {/* Icon */}
              <div className="mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
              </div>

              {/* Title */}
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Personalized AI Assistant Bot
              </h2>

              {/* Description */}
              <p className="text-gray-600 mb-6 flex-grow">
                Get a tailored interview experience customized to your specific role, resume, and job description. 
                The AI will provide personalized guidance and responses based on your unique profile.
              </p>

              {/* Features */}
              <div className="mb-6 space-y-3">
                <div className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mt-2 mr-3"></div>
                  <p className="text-sm text-gray-700">Upload your resume for analysis</p>
                </div>
                <div className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mt-2 mr-3"></div>
                  <p className="text-sm text-gray-700">Enter job title and description</p>
                </div>
                <div className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mt-2 mr-3"></div>
                  <p className="text-sm text-gray-700">Customize AI behavior to your needs</p>
                </div>
                <div className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mt-2 mr-3"></div>
                  <p className="text-sm text-gray-700">Get role-specific interview preparation</p>
                </div>
              </div>

              {/* Badge */}
              <div className="mb-6">
                <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
                  Recommended for Job Seekers
                </span>
              </div>

              {/* Button */}
              <button
                onClick={() => handleModeSelect('personalized')}
                disabled={isInitializing}
                className="w-full flex items-center justify-center px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Get Started with Setup
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* General Knowledge Instant Assistant */}
          <div className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all transform hover:-translate-y-1 border-2 border-transparent hover:border-success-400">
            <div className="flex flex-col h-full">
              {/* Icon */}
              <div className="mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-success-500 to-success-600 rounded-2xl flex items-center justify-center">
                  <Bot className="w-8 h-8 text-white" />
                </div>
              </div>

              {/* Title */}
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                General Knowledge Instant Assistant
              </h2>

              {/* Description */}
              <p className="text-gray-600 mb-6 flex-grow">
                Jump straight into the action with instant access to a general-purpose interview assistant. 
                No setup required - start practicing immediately with general interview knowledge.
              </p>

              {/* Features */}
              <div className="mb-6 space-y-3">
                <div className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-success-600 rounded-full mt-2 mr-3"></div>
                  <p className="text-sm text-gray-700">No prerequisites or setup needed</p>
                </div>
                <div className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-success-600 rounded-full mt-2 mr-3"></div>
                  <p className="text-sm text-gray-700">Instant access to interview dashboard</p>
                </div>
                <div className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-success-600 rounded-full mt-2 mr-3"></div>
                  <p className="text-sm text-gray-700">General interview tips and guidance</p>
                </div>
                <div className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-success-600 rounded-full mt-2 mr-3"></div>
                  <p className="text-sm text-gray-700">Practice common interview scenarios</p>
                </div>
              </div>

              {/* Badge */}
              <div className="mb-6">
                <span className="inline-block px-3 py-1 bg-success-100 text-success-700 text-xs font-semibold rounded-full">
                  Quick Start - No Setup
                </span>
              </div>

              {/* Button */}
              <button
                onClick={() => handleModeSelect('general')}
                disabled={isInitializing}
                className="w-full flex items-center justify-center px-6 py-3 bg-success-600 text-white rounded-lg font-medium hover:bg-success-700 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isInitializing ? (
                  <>
                    <div className="animate-spin w-5 h-5 mr-2 border-2 border-white border-t-transparent rounded-full" />
                    Initializing...
                  </>
                ) : (
                  <>
                    Start Instantly
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-12">
        </div>
      </div>
    </div>
  );
};

